/**
 * Module for canonical events that are exposed via the public facing event api
 * @module
 */
var _ = require('optimizely-client-fn');
var di = require('core/di');
var AudienceDataStore = di.get('stores/audience_data');
var EventEmitter = require('core/event_emitter');
var EventEmitterEnums = require('core/constants/event_emitter');
var LazyProp = require('../lazy_prop');
var services = require('../translate_services');

/**
 * @param {Object} config
 * @param {Layer} config.layer
 * @param {DecisionTicket} config.decisionTicket
 * @param {Decision} config.decision
 */
exports.emitLayerDecided = function(config:any) {
  var audienceIds = config.decisionTicket ? config.decisionTicket.audienceIds : [];
  var audiences = _.map(audienceIds, function(audienceId) {
    return {
      id: audienceId,
      name: AudienceDataStore.get(audienceId).name
    };
  });

  var layerDecidedEvent = {
    type: EventEmitterEnums.TYPES.LIFECYCLE,
    name: 'layerDecided',
    data: _.extend(config, {audiences: audiences}),
  };

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('LayerDecidedApiEvent', layerDecidedEvent);
  }

  var campaignDecidedEvent = services.translateLayerEventToCampaignEvent(layerDecidedEvent);

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('CampaignDecidedApiEvent', campaignDecidedEvent);
  }

  EventEmitter.emit(layerDecidedEvent);
  EventEmitter.emit(campaignDecidedEvent);
};

/**
 * Occurs immediately when a view is activated either from the controller activation
 * or a manually activated view
 * @param {Object} config
 * @param {View} config.view
 */
exports.emitViewActivated = function(config:any) {
  var viewActivatedEvent = {
    type: EventEmitterEnums.TYPES.LIFECYCLE,
    name: 'viewActivated',
    data: config,
  };

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('ViewActivatedApiEvent', viewActivatedEvent);
  }

  var pageActivatedEvent = services.translateViewActivatedToPageActivated(viewActivatedEvent);

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('PageActivatedApiEvent', pageActivatedEvent);
  }

  // TODO attach any additional data we may need such as audience name or view state
  EventEmitter.emit(viewActivatedEvent);
  EventEmitter.emit(pageActivatedEvent);
};

/**
 * Occurs after a group of views (of one activation type) are activated.
 * @param {Object} config
 * @param {View[]} config.views
 */
exports.emitViewsActivated = function(config:any) {
  var viewsActivatedEvent = {
    type: EventEmitterEnums.TYPES.LIFECYCLE,
    name: 'viewsActivated',
    data: config,
  };

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('ViewsActivatedApiEvent', viewsActivatedEvent);
  }

  // TODO attach any additional data we may need such as audience name or view state
  EventEmitter.emit(viewsActivatedEvent);
};


/**
 * Occurs immediately when a page is deactivated.
 * Note that the actual type being passed here is a
 * View, not a Page, per core/view/index.js
 *
 * @param {Object} config
 * @param {View} config.page
 */
exports.emitPageDeactivated = function(config:any) {
  var pageDeactivatedEvent = {
    type: EventEmitterEnums.TYPES.LIFECYCLE,
    name: 'pageDeactivated',
    data: config,
  };

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('PageDeactivatedApiEvent', pageDeactivatedEvent);
  }

  // TODO attach any additional data we may need such as audience name or view state
  EventEmitter.emit(pageDeactivatedEvent);
};

exports.emitActivateEvent = function() {
  // TODO(tyler): maybe generate activationId here instead of controller
  // TODO(tyler): rename this to be more distinct from "activated"
  EventEmitter.emit({
    type: EventEmitterEnums.TYPES.LIFECYCLE,
    name: 'activate',
  }, true);
};

/**
 * Emit an event to indicate activate is "done"
 */
exports.emitActivatedEvent = function() {
  EventEmitter.emit({
    type: EventEmitterEnums.TYPES.LIFECYCLE,
    name: 'activated',
  });
};

/**
 * Emit an event after initialize, before activate.
 */
exports.emitInitializedEvent = function() {
  var initializedEvent = {
    type: EventEmitterEnums.TYPES.LIFECYCLE,
    name: 'initialized',
  };

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('InitializedApiEvent', initializedEvent);
  }

  // Signal to any other clients that an Optimizely client has already initialized,
  // so that they may abort their initialization
  if (__GLOBAL__) {
    __GLOBAL__.initialized = true;
  }

  EventEmitter.emit(initializedEvent);
};

/**
 * Emit an event after the initializeXDomainStorage promise resolves/rejects.
 *
 * This event may not be emitted at all if, for example, the x-origin iframe fails to load. Callers may therefore need
 * to race this event against a timeout promise in order to guarantee execution of the desired code.
 */
exports.emitOriginsSyncedEvent = function() {
  var originsSyncedEvent = {
    type: EventEmitterEnums.TYPES.LIFECYCLE,
    name: 'originsSynced',
  };

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('OriginsSyncedEvent', originsSyncedEvent);
  }

  EventEmitter.emit(originsSyncedEvent);
};

/**
 * Emit an event after changeset tied to a single action has been executed.
 * @param {ActionData} action
 */
exports.emitActionAppliedEvent = function(action:ActionData) {
  var data = {
    type: action.type,
    campaignId: action.layerId,
    pageId: action.pageId,
    experimentId: action.experimentId,
    variationId: action.variationId,
  };

  LazyProp.defineProperty(data, 'changes', function() {
    return filterActionChangeset(action).changeSet;
  }, 'actionAppliedEvent');

  var actionAppliedEvent = {
    type: EventEmitterEnums.TYPES.ACTION,
    name: 'applied',
    data: data,
  };

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('ActionAppliedApiEvent', actionAppliedEvent);
  }

  /**
   * @param {Object} event
   * @param {string} event.type "action"
   * @param {string} event.name "applied"
   * @param {ActionAppliedEventData} event.data
   */
  EventEmitter.emit(actionAppliedEvent);
};

/**
 * Emit an event after all actions for a given decision have been applied.
 * @param {Decision} decision
 * @param {ActionData[]} actions
 */
exports.emitActionsForDecisionAppliedEvent = function(decision:Decision, actions:ActionData[]) {
  var data = {
    decision: decision,
  };

  LazyProp.defineProperty(data, 'actions', function() {
    return _.map(actions, filterActionChangeset);
  }, 'appliedAllForDecisionEvent');

  var actionsForDecisionAppliedEvent = {
    type: EventEmitterEnums.TYPES.ACTION,
    name: 'appliedAllForDecision',
    data: data,
  };

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('ActionsForDecisionAppliedApiEvent', actionsForDecisionAppliedEvent);
  }

  /**
   * @param {Object} event
   * @param {string} event.type "action"
   * @param {string} event.name "appliedAllForDecision"
   * @param {ActionsForDecisionAppliedEventData} event.data
   */
  EventEmitter.emit(actionsForDecisionAppliedEvent);
};

/**
 * Emit event to trigger sending of tracker_optimizely events
 */
exports.emitSendEvents = function() {
  var triggerSendEvents = {
    type: EventEmitterEnums.TYPES.ANALYTICS,
    name: 'sendEvents'
  };

  if(__INVARIANT__) {
    require('core/utils/invariant').isType('SendEventsApiEvent', triggerSendEvents);
  }

  EventEmitter.emit(triggerSendEvents);
};

/**
 * Emit event to trigger sending of tracker_optimizely events
 */
exports.emitMaybeSendEvents = function() {
  var triggerMaybeSendEvents = {
    type: EventEmitterEnums.TYPES.ANALYTICS,
    name: 'maybeSendEvents'
  };

  EventEmitter.emit(triggerMaybeSendEvents);
};

/**
 * Emit event to trigger holding of tracker_optimizely events
 */
exports.emitHoldEvents = function() {
  var triggerHoldEvents = {
    type: EventEmitterEnums.TYPES.ANALYTICS,
    name: 'holdEvents',
  };

  EventEmitter.emit(triggerHoldEvents);
};

/**
 * Helper function to filter fields from all changes in
 * an action based on a whitelist for public consumption.
 * (We don't want to expose all change fields publicly, yet.)
 * @param {ActionData} action
 * @returns {ActionData} a copy of the filtered action
 * @private
 */
function filterActionChangeset(action:ActionData): ActionData {
  // This is a whitelist of public fields of all change types.
  var WHITELIST = ['type', 'selector', 'attributes', 'value'];
  var res = _.extend({}, action);
  res.changeSet = _.map(action.changeSet, function(changeOrChangeId) {
    return _.pick(services.dereferenceChangeId(changeOrChangeId), WHITELIST);
  });
  return res;
}
